import React from "react"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import { Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import {
  asWebUrl,
  type ConsumerSubscriptionFacts,
  storefrontMessage
} from "#/services/consumerSubscription/externalStorefront"
import { reportErrorMessage } from "#/utils/ErrorReporting"

// Names the store when carrot-app has a name for it ("UHC Store"), per the design's "Visit the UHC store";
// the generic wording otherwise. The name is a plain-string ICU value, so it renders as text.
const BODY_NAMED = defineMessage({
  defaultMessage:
    "You bought Carrot through the {storeName}. To change or cancel your subscription, or to see your orders, visit the {storeName}."
})
const BODY_GENERIC = defineMessage({
  defaultMessage:
    "You bought Carrot through another store. To change or cancel your subscription, or to see your orders, go to that store."
})

/**
 * What an external-storefront member gets in place of Carrot's Stripe billing: the store's own pages for
 * changing or cancelling the subscription and for its orders and receipts. Every subscription change for
 * these members happens in the store (UHC Store System Manual v3.7, pp. 18 and 22), so the FE only ever
 * links to the urls carrot-app hands it — it never builds them. The store is named only by the
 * `displayName` carrot-app sends; with none, the copy stays generic.
 *
 * A url carrot-app sent as null (the store isn't configured yet, CAP-285) hides its link and is reported,
 * the same way login reports a missing storefront url. Opened in a new tab, like the page's other links
 * off Carrot, so the member keeps their Carrot session.
 *
 * Shared by account settings and the chromeless billing page.
 */
export function ExternalStorefrontSubscriptionLinks({
  storefront
}: {
  storefront: ConsumerSubscriptionFacts["storefront"]
}): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const body = storefrontMessage(storefront, { named: BODY_NAMED, generic: BODY_GENERIC })
  // Only web urls become links: an href is executable (`javascript:`), and these arrive from the server
  // verbatim. Anything else is treated as not configured.
  const manageSubscriptionUrl = asWebUrl(storefront.manageSubscriptionUrl)
  const orderHistoryUrl = asWebUrl(storefront.orderHistoryUrl)

  React.useEffect(() => {
    if (!manageSubscriptionUrl) {
      reportErrorMessage("External-storefront member has no manage-subscription url")
    }
    if (!orderHistoryUrl) {
      reportErrorMessage("External-storefront member has no order-history url")
    }
  }, [manageSubscriptionUrl, orderHistoryUrl])

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
      <Typography>{intl.formatMessage(body.message, body.values)}</Typography>
      {manageSubscriptionUrl && (
        <Link href={manageSubscriptionUrl} target="_blank" rel="noopener noreferrer">
          <FormattedMessage defaultMessage="Manage subscription" />
        </Link>
      )}
      {orderHistoryUrl && (
        <Link href={orderHistoryUrl} target="_blank" rel="noopener noreferrer">
          <FormattedMessage defaultMessage="View order history" />
        </Link>
      )}
    </Stack>
  )
}
